import React, { useState, useEffect, useRef } from 'react';
import { m } from 'motion/react';
import { Calculator as CalcIcon, Info, TrendingUp, Wallet, ReceiptText, CheckCircle2, Mail, ArrowRight, AlertCircle, Check, ArrowDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

// Mirrors the Xano fixed_tax_values response for the default year (2026), so the
// calculator can render immediately on first load instead of blocking on a network
// round trip that's on the critical path. Update this if the 2026 figures change in
// Xano; years other than the default still fetch live and get cached after first use.
const DEFAULT_TAX_VALUES_2026: Record<string, number> = {
  Grenzsteuersatz_Steuerklasse_1: 0,
  Grenzsteuersatz_Steuerklasse_2: 0.2,
  Grenzsteuersatz_Steuerklasse_3: 0.3,
  Grenzsteuersatz_Steuerklasse_4: 0.4,
  Grenzsteuersatz_Steuerklasse_5: 0.48,
  Grenzsteuersatz_Steuerklasse_6: 0.5,
  Grenzsteuersatz_Steuerklasse_7: 0.55,
  gfg_grenze: 551.1,
  gfb_grenze1: 178000,
  gfb_grenze2: 353000,
  gfb_grenze3: 583000,
  grundfreibetrag_satz: 0.15,
  gfb_satz1: 0.13,
  gfb_satz2: 0.07,
  gfb_satz3: 0.045,
  svs_kv: 0.068,
  svs_pv: 0.185,
  svs_svs: 0.0153,
  Steuerklasse_1: 0,
  Steuerklasse_2: 13539,
  Steuerklasse_3: 21992,
  Steuerklasse_4: 36458,
  Steuerklasse_5: 70365,
  Steuerklasse_6: 104859,
  Steuerklasse_7: 1000000,
  grenze_bap: 420000,
  grenze_kup: 55000,
  hbg_grenze_m: 6930,
  hbg_grenze: 97020,
  grundfreibetrag_grenze: 33000,
  svs_uv_b: 12.07,
  negativsteuer_percent: 0.55,
  steuer_sonst_grenze1: 620,
  steuer_sonst_grenze2: 24380,
  steuer_sonst_grenze3: 25000,
  steuer_sonst_grenze4: 33333,
  steuer_sonst_satz1: 0,
  steuer_sonst_satz2: 0.06,
  steuer_sonst_satz3: 0.27,
  steuer_sonst_satz4: 0.3575,
  fabo: 2000,
  fabo_halb: 1000.08,
  uhab_next: 73,
  kmb: 700,
  kilometergeld: 0.5,
  wkp: 132,
  fabo_18: 700,
  fabo_18_halb: 350,
  vab: 496,
  vab_untergrenze: 19761,
  vab_obergrenze: 30259,
  vab_zuschlag: 804,
  vab2_untergrenze: 15069,
  vab2_obergrenze: 16056,
  vab2: 853,
  uhab: 38,
  uhab2: 56,
  avab: 612,
  avab2: 828,
  avab_next: 273,
  negativsteuer_max: 1300,
  negativsteuer_pp_max: 1554,
  steuer_sonst_freigrenze: 2615,
  bap_satz_klein: 0.06,
  bap_satz_groß: 0.15,
};

export const Calculator: React.FC = () => {
  const { t, language } = useLanguage();
  const calcT = t.calculator;
  const [taxValues, setTaxValues] = useState<any>(DEFAULT_TAX_VALUES_2026);
  const taxValuesCache = useRef<Record<number, Record<string, number>>>({ 2026: DEFAULT_TAX_VALUES_2026 });
  const [selectedYear, setSelectedYear] = useState<number>(2026);
  const [state, setState] = useState({
    revenue: 60000,
    manualExpenses: 0,
    isPauschale: true,
    workFromHome: false,
    children: 0,
    results: {
      profit: 0,
      svs: 0,
      gfb: 0, // Profit allowance
      taxableProfit: 0,
      tax: 0,
      net: 0,
      monthly: 0,
      effectiveRate: 0
    }
  });

  const years = [2026, 2025, 2024, 2023, 2022];

  const sectionRef = useRef<HTMLElement>(null);
  const hasSentViewRef = useRef(false);
  const hasSentStartRef = useRef(false);
  const hasSentResultRef = useRef(false);

  // Lead Capture States
  const [leadEmail, setLeadEmail] = useState('');
  const [leadConsent, setLeadConsent] = useState(false);
  const [leadWebsite, setLeadWebsite] = useState(''); // Honeypot
  const [leadStep, setLeadStep] = useState<'idle' | 'form' | 'submitting' | 'doi_sent' | 'sent' | 'error'>('idle');
  const [leadError, setLeadError] = useState('');

  const openLeadForm = () => {
    setLeadStep('form');
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push({
      event: 'calculator_lead_open',
      language: language
    });
  };

  const handleSubmitLead = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadEmail || !leadConsent) return;

    setLeadStep('submitting');
    setLeadError('');

    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push({
      event: 'calculator_lead_submit',
      language: language
    });

    try {
      const response = await fetch('/api/calculator-lead', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: leadEmail,
          language,
          consent: leadConsent,
          website: leadWebsite,
          year: selectedYear,
          inputs: {
            revenue: state.revenue,
            isPauschale: state.isPauschale,
            manualExpenses: state.manualExpenses,
            workFromHome: state.workFromHome,
            children: state.children
          },
          results: {
            profit: state.results.profit,
            svs: state.results.svs,
            gfb: state.results.gfb,
            taxableProfit: state.results.taxableProfit,
            tax: state.results.tax,
            net: state.results.net,
            monthly: state.results.monthly,
            effectiveRate: state.results.effectiveRate,
            familyBonus: state.children * (Number(taxValues?.fabo) || 2000)
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errMsg = errorData.error || (calcT as any).lead?.errorGeneric || 'An error occurred';
        setLeadError(errMsg);
        setLeadStep('error');
        (window as any).dataLayer.push({
          event: 'calculator_lead_error',
          error_message: errMsg
        });
        return;
      }

      const data = await response.json();
      if (data.status === 'doi_sent') {
        setLeadStep('doi_sent');
      } else {
        setLeadStep('sent');
      }

      (window as any).dataLayer.push({
        event: 'calculator_lead_success',
        lead_status: data.status
      });

    } catch (error) {
      console.error('Lead submit error:', error);
      const errMsg = (calcT as any).lead?.errorGeneric || 'An error occurred';
      setLeadError(errMsg);
      setLeadStep('error');
      (window as any).dataLayer.push({
        event: 'calculator_lead_error',
        error_message: 'Network error or connection failed'
      });
    }
  };

  const langStr = language ? language.toLowerCase() : 'unknown';

  const triggerCalculatorStart = () => {
    if (!hasSentStartRef.current) {
      hasSentStartRef.current = true;
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push({
        event: 'calculator_start',
        page_path: window.location.pathname,
        language: langStr
      });
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasSentViewRef.current) {
            hasSentViewRef.current = true;
            (window as any).dataLayer = (window as any).dataLayer || [];
            (window as any).dataLayer.push({
              event: 'calculator_view',
              page_path: window.location.pathname,
              language: langStr
            });
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [langStr]);

  useEffect(() => {
    if (!taxValues) return;
    
    if (!hasSentResultRef.current) {
      hasSentResultRef.current = true;
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push({
        event: 'calculator_result_view',
        calculator_type: 'main_calculator',
        page_path: window.location.pathname,
        language: langStr
      });
    }
  }, [taxValues, langStr]);

  useEffect(() => {
    const cached = taxValuesCache.current[selectedYear];
    if (cached) {
      setTaxValues(cached);
      return;
    }
    fetch(`https://x0i2-mqpq-y2xk.f2.xano.io/api:Yy7FU-4j/fixed_tax_values?year=${selectedYear}`)
      .then(res => res.json())
      .then(data => {
        const values = data.reduce((acc: any, curr: any) => {
          acc[curr.key] = curr.value;
          return acc;
        }, {});
        taxValuesCache.current[selectedYear] = values;
        setTaxValues(values);
      })
      .catch(err => console.error('Error fetching tax values:', err));
  }, [selectedYear]);

  useEffect(() => {
    if (!taxValues) return;

    const v = taxValues;
    const revenue = Number(state.revenue) || 0;
    
    // 1. Calculate Expenses (Pauschale logic per user request)
    // If revenue <= grenze_kup (55k), use 20%. Else use bap_satz_groß (15% in 2026).
    const grenzeKup = Number(v.grenze_kup) || 0;
    const bapSatzGross = Number(v.bap_satz_groß) || 0;
    const pauschaleRate = revenue <= grenzeKup ? 0.20 : bapSatzGross;
    const pauschaleAmount = state.isPauschale ? revenue * pauschaleRate : 0;
    const manualExpenses = !state.isPauschale ? Number(state.manualExpenses) || 0 : 0;
    
    // Work from home allowance (€ 1.200)
    const wfhAllowance = state.workFromHome ? 1200 : 0;

    // 2. Profit before SVS and GFB
    const profitBeforeDeductions = Math.max(0, revenue - pauschaleAmount - manualExpenses - wfhAllowance);

    // 3. Profit Allowance (Gewinnfreibetrag - GFB)
    const gfbProfit = profitBeforeDeductions;
    
    // Grundfreibetrag (15% up to 33.000)
    const gfbGrenze = Number(v.grundfreibetrag_grenze) || 0;
    const gfbSatz = Number(v.grundfreibetrag_satz) || 0;

    const gfb = Math.min(gfbProfit, gfbGrenze) * gfbSatz;

    const profitAfterGFB = Math.max(0, profitBeforeDeductions - gfb);

    // 4. SVS Calculation
    const svsRate = (Number(v.svs_kv) || 0) + (Number(v.svs_pv) || 0) + (Number(v.svs_svs) || 0);
    const hbgGrenze = Number(v.hbg_grenze) || 0;
    const svsUvB = Number(v.svs_uv_b) || 0;
    const svsBasis = Math.min(profitAfterGFB, hbgGrenze);
    const svs = (svsBasis * svsRate) + (svsUvB * 12);
    
    // 5. Income Tax Calculation (Taxable Income)
    const taxableIncome = Math.max(0, profitAfterGFB - svs);
    let tax = 0;

    // We expect the API to provide Steuerklasse_1 (threshold for 20%), Steuerklasse_2, etc.
    // The first bracket is always 0% up to Steuerklasse_1 (which is the tax-free limit).
    const brackets = [
      { g: 0, r: 0 },
      { g: Number(v.Steuerklasse_1) || 0, r: Number(v.Grenzsteuersatz_Steuerklasse_1) || 0 },
      { g: Number(v.Steuerklasse_2) || 0, r: Number(v.Grenzsteuersatz_Steuerklasse_2) || 0 },
      { g: Number(v.Steuerklasse_3) || 0, r: Number(v.Grenzsteuersatz_Steuerklasse_3) || 0 },
      { g: Number(v.Steuerklasse_4) || 0, r: Number(v.Grenzsteuersatz_Steuerklasse_4) || 0 },
      { g: Number(v.Steuerklasse_5) || 0, r: Number(v.Grenzsteuersatz_Steuerklasse_5) || 0 },
      { g: Number(v.Steuerklasse_6) || 0, r: Number(v.Grenzsteuersatz_Steuerklasse_6) || 0 }
    ];

    for (let i = 0; i < brackets.length; i++) {
      const currentBracket = brackets[i].g;
      const nextBracket = i < brackets.length - 1 ? brackets[i + 1].g : Infinity;
      const rate = brackets[i].r;

      if (taxableIncome > currentBracket) {
        const taxableInThisTier = Math.min(taxableIncome, nextBracket) - currentBracket;
        tax += taxableInThisTier * rate;
      }
    }

    // 6. Tax Credits (Family Bonus Plus: € 2.000 per child)
    const fabo = Number(v.fabo) || 0;
    const childBonus = Math.min(tax, state.children * fabo);
    tax = Math.max(0, tax - childBonus);

    const net = revenue - svs - tax - (state.isPauschale ? 0 : manualExpenses);

    setState(prev => ({ 
      ...prev, 
      results: {
        profit: profitBeforeDeductions,
        svs,
        gfb,
        taxableProfit: taxableIncome,
        tax,
        net,
        monthly: net / 12,
        effectiveRate: revenue > 0 ? Math.ceil(((svs + tax) / revenue) * 100) : 0
      }
    }));
  }, [state.revenue, state.manualExpenses, state.isPauschale, state.workFromHome, state.children, taxValues]);

  const updateState = (key: string, value: any) => setState(prev => ({ ...prev, [key]: value }));

  const formatNum = (num: number) => {
    return new Intl.NumberFormat('de-AT', {
      maximumFractionDigits: 0,
      useGrouping: true
    }).format(Math.round(num));
  };

  return (
    <section ref={sectionRef} id="calculator" className="py-20 bg-pstax-bg scroll-mt-20 overflow-hidden">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col items-center justify-center gap-4 mb-10">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-pstax-navy rounded-xl flex items-center justify-center text-white shrink-0">
                <CalcIcon size={20} />
              </div>
              <h2 className="text-3xl font-display font-bold text-pstax-navy text-center">{calcT.title}</h2>
            </div>
            <p className="text-pstax-gray text-base max-w-3xl text-center">{calcT.subheadline}</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-start">
            {/* Input Column */ }
            <div className="bg-white/95 border border-pstax-border rounded-[2.5rem] p-8 flex flex-col gap-8">
              <div className="space-y-8">
                {/* Revenue Slider */}
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <label htmlFor="revenue-range" className="text-xs font-black text-pstax-navy uppercase tracking-widest opacity-60">{calcT.revenueLabel}</label>
                    <span className="text-4xl font-display font-bold text-pstax-navy">€ {formatNum(state.revenue)}</span>
                  </div>
                  <input 
                    id="revenue-range"
                    type="range" 
                    min="10000" 
                    max="300000" 
                    step="1000"
                    value={state.revenue}
                    onChange={(e) => {
                      triggerCalculatorStart();
                      updateState('revenue', parseInt(e.target.value));
                    }}
                    className="w-full h-2 bg-pstax-border rounded-lg appearance-none cursor-pointer accent-pstax-navy"
                  />
                </div>

                {/* Expenses & Allowances */}
                <div className="space-y-4">
                  <span className="block text-xs font-black text-pstax-navy uppercase tracking-widest opacity-60">{calcT.expensesLabel}</span>
                  
                  <div className="grid grid-cols-1 gap-3">
                    <div className="group relative">
                      <button 
                        onClick={() => {
                          triggerCalculatorStart();
                          updateState('isPauschale', !state.isPauschale);
                        }}
                        className={`w-full flex items-center justify-between p-4 rounded-xl border text-left transition-all ${state.isPauschale ? 'bg-pstax-navy text-white border-pstax-navy shadow-lg' : 'bg-white border-pstax-border text-pstax-navy hover:border-pstax-blue'}`}
                        aria-pressed={state.isPauschale}
                      >
                        <div className="flex items-center gap-3">
                          <ReceiptText size={20} className={state.isPauschale ? 'text-pstax-blue' : 'text-pstax-gray'} />
                          <span className="font-bold">
                            {calcT.pauschaleLabel.replace('{rate}', String(state.revenue <= (Number(taxValues?.grenze_kup) || 0) ? 20 : (Number(taxValues?.bap_satz_groß) || 0) * 100))}
                          </span>
                        </div>
                        {state.isPauschale ? <CheckCircle2 size={18} className="text-pstax-blue" /> : <div className="text-[10px] uppercase font-black text-pstax-blue opacity-0 group-hover:opacity-100 transition-opacity">{t.common.select}</div>}
                      </button>
                      {state.isPauschale && (
                        <div className="absolute -bottom-14 left-1/2 -translate-x-1/2 w-64 max-w-[80vw] bg-pstax-navy text-white text-[10px] sm:text-xs p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20 shadow-xl text-center">
                          {calcT.isPauschaleTooltip}
                        </div>
                      )}
                    </div>

                    {!state.isPauschale && (
                      <m.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} className="space-y-1">
                         <label htmlFor="actual-expenses" className="text-xs font-black uppercase tracking-widest text-pstax-blue ml-1">{calcT.actualExpensesLabel}</label>
                         <input 
                          id="actual-expenses"
                          type="number" 
                          value={state.manualExpenses}
                          onChange={(e) => {
                            triggerCalculatorStart();
                            updateState('manualExpenses', parseInt(e.target.value) || 0);
                          }}
                          className="w-full bg-white border-2 border-pstax-blue p-4 rounded-xl outline-none font-mono text-pstax-navy text-lg"
                        />
                      </m.div>
                    )}

                    <button 
                      onClick={() => {
                        triggerCalculatorStart();
                        updateState('workFromHome', !state.workFromHome);
                      }}
                      className={`flex items-center justify-between p-4 rounded-xl border text-left transition-all ${state.workFromHome ? 'bg-pstax-navy text-white border-pstax-navy shadow-lg' : 'bg-white border-pstax-border text-pstax-navy hover:border-pstax-blue'}`}
                      aria-pressed={state.workFromHome}
                    >
                      <div className="flex items-center gap-3">
                        <TrendingUp size={20} className={state.workFromHome ? 'text-pstax-blue' : 'text-pstax-gray'} />
                        <span className="font-bold">{calcT.wfhLabel}</span>
                      </div>
                      {state.workFromHome && <span className="text-xs font-mono opacity-60">+€ 1.200</span>}
                    </button>

                    <div className="flex gap-3">
                      <div className="flex-1 flex items-center gap-3 p-4 bg-white border border-pstax-border rounded-xl text-pstax-navy">
                         <CalcIcon size={20} className="text-pstax-blue" />
                         <span className="font-bold">{calcT.childrenLabel}</span>
                      </div>
                      <div className="flex items-center gap-2 bg-white border border-pstax-border rounded-xl p-2 px-4 shadow-sm">
                        <button 
                          onClick={() => {
                            triggerCalculatorStart();
                            updateState('children', Math.max(0, state.children - 1));
                          }}
                          className="w-8 h-8 rounded-lg bg-pstax-bg flex items-center justify-center text-pstax-navy hover:bg-pstax-border transition-colors font-bold"
                          aria-label="Decrease child count"
                        >
                          -
                        </button>
                        <span className="w-8 text-center font-display font-bold text-xl text-pstax-navy" aria-live="polite">{state.children}</span>
                        <button 
                          onClick={() => {
                            triggerCalculatorStart();
                            updateState('children', Math.min(10, state.children + 1));
                          }}
                          className="w-8 h-8 rounded-lg bg-pstax-bg flex items-center justify-center text-pstax-navy hover:bg-pstax-border transition-colors font-bold"
                          aria-label="Increase child count"
                        >
                          +
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center justify-center gap-3 pt-6 mt-2 border-t border-pstax-border/30">
                      <label htmlFor="tax-year-select" className="text-[10px] font-black text-pstax-navy uppercase tracking-[0.2em] opacity-40">{calcT.taxYearLabel}</label>
                      <select 
                        id="tax-year-select"
                        value={selectedYear}
                        onChange={(e) => {
                          triggerCalculatorStart();
                          setSelectedYear(parseInt(e.target.value));
                        }}
                        className="bg-white border border-pstax-border p-2 px-4 rounded-lg font-bold text-pstax-navy text-xs outline-none focus:border-pstax-blue"
                      >
                        {years.map(y => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-8">
                <div className="p-5 bg-pstax-bg rounded-2xl border border-pstax-border text-center">
                  <div className="flex items-start gap-4 justify-center">
                    <Info size={16} className="text-pstax-blue shrink-0 mt-0.5" />
                    <p className="text-xs text-pstax-gray italic leading-relaxed max-w-sm">
                      {calcT.disclaimer.replace('{year}', String(selectedYear))}
                    </p>
                  </div>
                </div>

                <a 
                  href="#fit-check" 
                  onClick={(e) => {
                    const fitCheckSection = document.getElementById('fit-check');
                    if (fitCheckSection) {
                      e.preventDefault();
                      fitCheckSection.scrollIntoView({ behavior: 'smooth' });
                    }
                    (window as any).dataLayer = (window as any).dataLayer || [];
                    (window as any).dataLayer.push({
                      event: 'calculator_cta_click',
                      cta_text: calcT.cta,
                      cta_location: 'calculator',
                      page_path: window.location.pathname,
                      language: langStr
                    });
                  }}
                  className="btn-accent w-full py-3.5 text-center flex items-center justify-center gap-2 group text-sm"
                >
                  {calcT.cta} <TrendingUp size={16} className="group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </div>

            {/* Results Column */}
            <div className="bg-pstax-navy text-white rounded-[2.5rem] p-10 flex flex-col justify-between shadow-2xl relative overflow-hidden">
               <div className="absolute top-0 right-0 p-10 opacity-5">
                  <CalcIcon size={180} />
               </div>

               <div className="relative z-10">
                 <div className="space-y-4 mb-8">
                   <div className="flex justify-between items-center py-3 border-y border-white/10 text-white">
                     <span className="text-xs font-bold uppercase tracking-widest opacity-60">{calcT.revenueHeader}</span>
                     <span className="font-display font-bold text-xl">€ {formatNum(state.revenue)}</span>
                   </div>
                   
                   {state.isPauschale && (
                    <div className="flex justify-between items-center text-emerald-400 text-sm">
                      <span>{calcT.pauschaleShort}</span>
                      <span className="font-mono">-€ {formatNum(state.revenue * (state.revenue <= (Number(taxValues?.grenze_kup) || 0) ? 0.20 : (Number(taxValues?.bap_satz_groß) || 0)))}</span>
                    </div>
                   )}

                   {!state.isPauschale && state.manualExpenses > 0 && (
                    <div className="flex justify-between items-center text-emerald-400 text-sm">
                      <span>{calcT.actualShort}</span>
                      <span className="font-mono">-€ {formatNum(state.manualExpenses)}</span>
                    </div>
                   )}

                   {state.workFromHome && (
                    <div className="flex justify-between items-center text-emerald-400 text-sm">
                      <span>{calcT.wfhShort}</span>
                      <span className="font-mono">-€ 1.200</span>
                    </div>
                   )}

                   <div className="flex justify-between items-center py-3 border-y border-white/10 text-white">
                     <span className="text-xs font-bold uppercase tracking-widest opacity-60">{calcT.expectedProfit}</span>
                     <span className="font-display font-bold text-xl">€ {formatNum(state.results.profit)}</span>
                   </div>

                   <div className="flex justify-between items-center text-emerald-400 text-sm">
                     <span>{calcT.gfbLabel}</span>
                     <span className="font-mono">-€ {formatNum(state.results.gfb)}</span>
                   </div>
                   
                   <div className="flex justify-between items-center text-red-500 text-sm">
                     <span>{calcT.svsLabel}</span>
                     <span className="font-mono">-€ {formatNum(state.results.svs)}</span>
                   </div>                 

                   <div className="flex justify-between items-center py-3 border-y border-white/10 text-white">
                     <span className="text-xs font-bold uppercase tracking-widest opacity-60">{calcT.taxableProfitLabel}</span>
                     <span className="font-display font-bold text-xl">€ {formatNum(state.results.taxableProfit)}</span>
                   </div>

                   {state.children > 0 && (
                    <div className="flex justify-between items-center text-emerald-400 text-sm">
                      <span>{calcT.familyBonusShort}</span>
                      <span className="font-mono">-€ {formatNum(state.children * (taxValues?.fabo || 2000))}</span>
                    </div>
                   )}

                   <div className="flex justify-between items-center text-red-500 text-sm">
                     <span>{calcT.taxLabel}</span>
                     <span className="font-mono">-€ {formatNum(state.results.tax)}</span>
                   </div>
                 </div>

                 <div className="space-y-6 cursor-default">
                   <div className="border-t border-white/10 pt-6 text-center">
                     <p className="text-[10px] font-black uppercase tracking-[0.1em] text-pstax-blue mb-1">{calcT.annualNetLabel}</p>
                     <p className="text-5xl md:text-6xl font-display font-bold text-white tracking-tighter">€ {formatNum(state.results.net)}</p>
                   </div>

                   <div className="grid grid-cols-2 border-t border-white/10 pt-4 divide-x divide-white/10">
                     <div className="text-center px-2">
                       <p className="text-[10px] font-black uppercase tracking-[0.1em] text-white/40 mb-1 leading-none">{calcT.monthlyNetLabel}</p>
                       <p className="text-xl md:text-2xl font-display font-bold text-white">€ {formatNum(state.results.monthly)}</p>
                     </div>
                     <div className="text-center px-2">
                       <p className="text-[10px] font-black uppercase tracking-[0.1em] text-white/40 mb-1 leading-none">{calcT.effectiveRateLabel}</p>
                       <p className="text-xl md:text-2xl font-display font-bold text-white">{state.results.effectiveRate}%</p>
                     </div>
                   </div>

                    {/* Compact Lead Form inside Results Column */}
                    <div className="mt-4 p-5 bg-white/5 border border-white/10 rounded-2xl text-left space-y-3.5">
                      {leadStep === 'idle' ? (
                        <div className="space-y-3">
                          <div className="space-y-1">
                            <h4 className="text-sm font-bold text-white tracking-tight">
                              {language === 'DE' ? 'Verwirrt? Brauchst du mehr Infos?' : 'Confused? Do you want more details?'}
                            </h4>
                            <p className="text-[11px] text-white/60 leading-normal">
                              {language === 'DE'
                                ? 'Gerne senden wir dir deine Berechnung mit detaillierten Erklärungen kostenlos per E-Mail!'
                                : 'We will gladly send you your calculation with detailed explanations via e-mail for free!'}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={openLeadForm}
                            className="w-full bg-pstax-blue hover:bg-pstax-blue/90 text-pstax-navy font-bold py-2.5 px-4 rounded-xl text-xs flex items-center justify-center gap-2 transition-all active:scale-[0.98] shadow-md shadow-pstax-blue/5"
                          >
                            <Mail size={14} />
                            <span>{language === 'DE' ? 'Ergebnisse per E-Mail' : 'Get results by E-mail'}</span>
                            <ArrowRight size={12} />
                          </button>
                        </div>
                      ) : (
                        <m.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          className="space-y-3 overflow-hidden"
                        >
                          <div className="space-y-1">
                            <h4 className="text-sm font-bold text-white tracking-tight">
                              {language === 'DE' ? 'Berechnung anfordern' : 'Request calculation'}
                            </h4>
                          </div>

                          {(leadStep === 'form' || leadStep === 'submitting' || leadStep === 'error') && (
                            <form onSubmit={handleSubmitLead} className="space-y-3">
                              <div>
                                <input
                                  type="email"
                                  required
                                  disabled={leadStep === 'submitting'}
                                  placeholder={(calcT as any).lead?.emailPlaceholder || 'your.email@example.com'}
                                  value={leadEmail}
                                  onChange={(e) => setLeadEmail(e.target.value)}
                                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs text-white placeholder-white/30 focus:outline-none focus:border-pstax-blue focus:ring-1 focus:ring-pstax-blue transition-colors disabled:opacity-50"
                                />
                              </div>

                              {/* Honeypot field (hidden) */}
                              <input
                                type="text"
                                name="website"
                                tabIndex={-1}
                                autoComplete="off"
                                value={leadWebsite}
                                onChange={(e) => setLeadWebsite(e.target.value)}
                                className="hidden"
                              />

                              <label className="flex items-start gap-2 cursor-pointer group">
                                <input
                                  type="checkbox"
                                  required
                                  disabled={leadStep === 'submitting'}
                                  checked={leadConsent}
                                  onChange={(e) => setLeadConsent(e.target.checked)}
                                  className="mt-0.5 rounded border-white/20 bg-white/5 text-pstax-blue focus:ring-offset-pstax-navy focus:ring-pstax-blue h-3.5 w-3.5 transition-colors cursor-pointer shrink-0"
                                />
                                <span className="text-[10px] text-white/50 leading-normal select-none group-hover:text-white/70 transition-colors">
                                  {language === 'DE' ? (
                                    <>
                                      Ich stimme zu, dass PSTax mir meine Berechnungsergebnisse zusendet. Abmeldung jederzeit möglich. Details in der{' '}
                                      <Link to="/de/datenschutz/" className="underline hover:text-pstax-blue">
                                        Datenschutzerklärung
                                      </Link>
                                      .
                                    </>
                                  ) : (
                                    <>
                                      I agree to receive my calculation results. Unsubscribe anytime. See details in the{' '}
                                      <Link to="/en/privacy-policy/" className="underline hover:text-pstax-blue">
                                        Privacy Policy
                                      </Link>
                                      .
                                    </>
                                  )}
                                </span>
                              </label>

                              {leadStep === 'error' && (
                                <div className="p-2 bg-red-500/10 border border-red-500/20 text-red-400 rounded-lg text-[11px] flex items-start gap-1.5">
                                  <AlertCircle size={12} className="shrink-0 mt-0.5" />
                                  <span>{leadError || (calcT as any).lead?.errorGeneric}</span>
                                </div>
                              )}

                              <button
                                type="submit"
                                disabled={leadStep === 'submitting' || !leadConsent || !leadEmail}
                                className="w-full bg-pstax-blue hover:bg-pstax-blue/90 text-pstax-navy disabled:bg-white/20 disabled:text-white/40 disabled:cursor-not-allowed font-bold py-2.5 px-4 rounded-xl transition-all flex items-center justify-center gap-2 text-xs shadow-md shadow-pstax-blue/5"
                              >
                                {leadStep === 'submitting' ? (
                                  <>
                                    <svg className="animate-spin -ml-1 mr-2 h-3.5 w-3.5 text-pstax-navy" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    <span>{(calcT as any).lead?.buttonSubmitting || 'Sending...'}</span>
                                  </>
                                ) : (
                                  <span>{(calcT as any).lead?.buttonSubmit || 'Send Results'}</span>
                                )}
                              </button>
                            </form>
                          )}

                          {leadStep === 'doi_sent' && (
                            <div className="p-3 bg-pstax-blue/10 border border-pstax-blue/20 text-pstax-blue rounded-xl text-[11px] flex items-start gap-2">
                              <CheckCircle2 size={14} className="shrink-0 mt-0.5" />
                              <p className="font-bold">{(calcT as any).lead?.successDoiSent || 'Double opt-in initiated!'}</p>
                            </div>
                          )}

                          {leadStep === 'sent' && (
                            <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-xl text-[11px] flex items-start gap-2">
                              <CheckCircle2 size={14} className="shrink-0 mt-0.5" />
                              <p className="font-bold">{(calcT as any).lead?.successSent || 'Results sent!'}</p>
                            </div>
                          )}
                        </m.div>
                      )}
                    </div>

                 </div>
               </div>
            </div>
          </div>

          {/* Hidden for now as requested - Large Lead Card */}
          {false && (
            <div id="detailed-report-card" className="mt-10 bg-gradient-to-br from-pstax-navy to-[#0f1f3a] text-white rounded-[2.5rem] shadow-2xl relative overflow-hidden border border-white/10">
            {/* Visual glow accents for premium appearance */}
            <div className="absolute top-0 right-0 -mt-24 -mr-24 w-96 h-96 bg-pstax-blue/10 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 -mb-24 -ml-24 w-96 h-96 bg-pstax-blue/5 rounded-full blur-3xl pointer-events-none" />

            <div className="relative z-10 grid md:grid-cols-12 gap-8 items-center p-8 md:p-12">
              <div className="md:col-span-7 space-y-4 text-left">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] md:text-xs font-bold uppercase tracking-wider bg-pstax-blue/15 text-pstax-blue border border-pstax-blue/30 shadow-sm">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse shrink-0" />
                    {language === 'DE' ? 'Kostenloser Service' : 'Free Service'}
                  </span>
                </div>
                <div>
                  <h3 className="text-xl md:text-2xl font-display font-bold text-white leading-tight tracking-tight">
                    {(calcT as any).lead?.title || (language === 'DE' ? 'Berechnungsergebnisse per E-Mail' : 'Calculation results by E-mail')}
                  </h3>
                  <p className="text-xs md:text-sm text-white/70 mt-2 leading-relaxed max-w-xl">
                    {(calcT as any).lead?.description || (language === 'DE' ? 'Erhalte deine detaillierte Steuerberechnung als PDF-Report direkt in dein Postfach.' : 'Get your detailed tax calculation as a PDF report directly in your inbox.')}
                  </p>
                </div>

                {/* Benefits List: Standard list on desktop, trimmed tags on mobile */}
                <ul className="hidden md:block space-y-2.5 text-xs md:text-sm text-white/80 pt-2">
                  <li className="flex items-center gap-2.5">
                    <Check size={16} className="text-pstax-blue shrink-0" />
                    <span>{language === 'DE' ? 'Aufstellung aller SVS-Beiträge' : 'Breakdown of all SVS contributions'}</span>
                  </li>
                  <li className="flex items-center gap-2.5">
                    <Check size={16} className="text-pstax-blue shrink-0" />
                    <span>{language === 'DE' ? 'Steuerstufen & Grenzsteuersätze' : 'Tax brackets & marginal rates'}</span>
                  </li>
                  <li className="flex items-center gap-2.5">
                    <Check size={16} className="text-pstax-blue shrink-0" />
                    <span>{language === 'DE' ? 'Tipps zur Optimierung deiner Gewinne' : 'Tips to optimize your profits'}</span>
                  </li>
                </ul>

                {/* Trimmed visual tags for Mobile ONLY (highly compact to save space) */}
                <div className="md:hidden flex flex-wrap gap-2 text-[10px] text-white/80 pt-1">
                  <span className="inline-flex items-center gap-1 bg-white/5 border border-white/10 px-2 py-1 rounded-full">
                    <Check size={10} className="text-pstax-blue shrink-0" />
                    {language === 'DE' ? 'SVS-Beiträge' : 'SVS contributions'}
                  </span>
                  <span className="inline-flex items-center gap-1 bg-white/5 border border-white/10 px-2 py-1 rounded-full">
                    <Check size={10} className="text-pstax-blue shrink-0" />
                    {language === 'DE' ? 'Steuerstufen' : 'Tax brackets'}
                  </span>
                  <span className="inline-flex items-center gap-1 bg-white/5 border border-white/10 px-2 py-1 rounded-full">
                    <Check size={10} className="text-pstax-blue shrink-0" />
                    {language === 'DE' ? 'Optimierungs-Tipps' : 'Profit optimization'}
                  </span>
                </div>
              </div>

              <div className="md:col-span-5 w-full">
                {leadStep === 'idle' && (
                  <button
                    type="button"
                    onClick={openLeadForm}
                    className="w-full bg-pstax-blue hover:bg-pstax-blue/90 text-pstax-navy font-bold py-4 px-6 rounded-2xl active:scale-[0.98] transition-all flex items-center justify-center gap-2.5 group shadow-xl shadow-pstax-blue/20 text-base"
                    id="cta-get-results-email"
                  >
                    <Mail size={18} className="shrink-0" />
                    <span>{(calcT as any).lead?.subtitle || 'Get results by E-mail'}</span>
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform shrink-0" />
                  </button>
                )}

                {leadStep !== 'idle' && (
                  <m.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="space-y-4 overflow-hidden"
                  >
                    {(leadStep === 'form' || leadStep === 'submitting' || leadStep === 'error') && (
                      <form onSubmit={handleSubmitLead} className="space-y-3">
                        <div>
                          <input
                            type="email"
                            required
                            disabled={leadStep === 'submitting'}
                            placeholder={(calcT as any).lead?.emailPlaceholder || 'your.email@example.com'}
                            value={leadEmail}
                            onChange={(e) => setLeadEmail(e.target.value)}
                            className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-pstax-blue focus:ring-1 focus:ring-pstax-blue transition-colors disabled:opacity-50"
                          />
                        </div>

                        {/* Honeypot field (hidden) */}
                        <input
                          type="text"
                          name="website"
                          tabIndex={-1}
                          autoComplete="off"
                          value={leadWebsite}
                          onChange={(e) => setLeadWebsite(e.target.value)}
                          className="hidden"
                        />

                        <label className="flex items-start gap-2.5 cursor-pointer group">
                          <input
                            type="checkbox"
                            required
                            disabled={leadStep === 'submitting'}
                            checked={leadConsent}
                            onChange={(e) => setLeadConsent(e.target.checked)}
                            className="mt-1 rounded border-white/25 bg-white/5 text-pstax-blue focus:ring-offset-pstax-navy focus:ring-pstax-blue h-4 w-4 transition-colors cursor-pointer shrink-0"
                          />
                          <span className="text-[11px] text-white/60 leading-normal select-none group-hover:text-white/80 transition-colors text-left">
                            {language === 'DE' ? (
                              <>
                                Ich stimme zu, dass PSTax mir meine Berechnungsergebnisse und gelegentliche Steuer-Updates per E-Mail zusendet. Ich kann mich jederzeit abmelden. Details in der{' '}
                                <Link to="/de/datenschutz/" className="underline hover:text-pstax-blue font-bold">
                                  Datenschutzerklärung
                                </Link>
                                .
                              </>
                            ) : (
                              <>
                                I agree that PSTax may send me my calculation results and occasional tax updates via E-mail. I can unsubscribe at any time. See details in the{' '}
                                <Link to="/en/privacy-policy/" className="underline hover:text-pstax-blue font-bold">
                                  Privacy Policy
                                </Link>
                                .
                              </>
                            )}
                          </span>
                        </label>

                        {leadStep === 'error' && (
                          <div className="p-3 bg-red-500/10 border border-red-500/20 text-red-400 rounded-lg text-xs flex items-start gap-2 text-left">
                            <AlertCircle size={14} className="shrink-0 mt-0.5" />
                            <span>{leadError || (calcT as any).lead?.errorGeneric}</span>
                          </div>
                        )}

                        <button
                          type="submit"
                          disabled={leadStep === 'submitting' || !leadConsent || !leadEmail}
                          className="w-full bg-pstax-blue hover:bg-pstax-blue/90 text-pstax-navy disabled:bg-white/20 disabled:text-white/40 disabled:cursor-not-allowed font-bold py-3.5 px-4 rounded-xl transition-all flex items-center justify-center gap-2 text-sm shadow-lg shadow-pstax-blue/10"
                        >
                          {leadStep === 'submitting' ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-pstax-navy" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                              <span>{(calcT as any).lead?.buttonSubmitting || 'Sending...'}</span>
                            </>
                          ) : (
                            <span>{(calcT as any).lead?.buttonSubmit || 'Send Results'}</span>
                          )}
                        </button>
                      </form>
                    )}

                    {leadStep === 'doi_sent' && (
                      <div className="p-4 bg-pstax-blue/10 border border-pstax-blue/20 text-pstax-blue rounded-xl text-xs flex items-start gap-3 text-left">
                        <CheckCircle2 size={18} className="shrink-0 mt-0.5" />
                        <div className="space-y-1">
                          <p className="font-bold">{(calcT as any).lead?.successDoiSent || 'Double opt-in initiated!'}</p>
                        </div>
                      </div>
                    )}

                    {leadStep === 'sent' && (
                      <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-xl text-xs flex items-start gap-3 text-left">
                        <CheckCircle2 size={18} className="shrink-0 mt-0.5" />
                        <div className="space-y-1">
                          <p className="font-bold">{(calcT as any).lead?.successSent || 'Results sent!'}</p>
                        </div>
                      </div>
                    )}
                  </m.div>
                )}
              </div>
            </div>
          </div>
          )}
        </div>
      </div>
    </section>
  );
};
